import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Formik, Form, Field } from 'formik'
import _ from 'lodash'

import Modal from '../components/Modal'
import DeleteButton from '../components/DeleteButton'
import { IconEdit } from '../components/icons'
import { DatePicker } from '../components/input/DatePicker'
import { useLocalState } from '../settings/localStorage'
import { CostsGraph, CostsMainDriversGraph } from '../costs/CostsGraph'
import { PureCostsTable } from '../costs/CostsTable'
import { CostFormFields, CostFormFooter } from '../costs/CostFormFields'
import { useCosts, CostItem } from '../costs/CostsProvider'
import { useFxContext } from '../forecast/FxProvider'
import { useSingleTeamMangement } from '../Management'
import IpManagementBreadcrumbs from './IpManagementBreadcrumbs'

const IP_MANAGEMENT_ENTITY = 'ip-management'
const IP_MANAGEMENT_ENTITY_ID = 0

export default function ManagementCosts() {
    const [editingCostId, setEditingCostId] = useState<number | 'new' | null>(null)
    const [sortField, setSortField] = useLocalState('management.costs.sort-field', 'date')
    const [sortOrder, setSortOrder] = useLocalState('management.costs.sort-order', 1)

    const { costsByEntity, postCost, deleteCost, postCostLink, deleteCostLink, linkByCostId } = useCosts()
    const { fxConverter } = useFxContext()
    const { team } = useSingleTeamMangement()
    const ccy = team?.currency ?? 'EUR'

    const entries: CostItem[] = costsByEntity[IP_MANAGEMENT_ENTITY]?.[IP_MANAGEMENT_ENTITY_ID] ?? []

    const sorted = _.orderBy(entries, [c => (c as any)[sortField]], [sortOrder === 1 ? 'asc' : 'desc'])

    const byYear: Record<number, number> = {}
    entries.forEach(c => {
        const year = c.date ? new Date(c.date).getFullYear() : new Date().getFullYear()
        const native = c.amount + c.vat
        const conv = fxConverter.convert(native, c.currency || ccy, ccy) ?? native
        byYear[year] = Math.round((byYear[year] ?? 0) + conv)
    })

    const mainDrivers = _(entries)
        .groupBy(c => c.reference || '—')
        .map((items, name) => ({
            name,
            total: _.sumBy(items, c => {
                const native = c.amount + c.vat
                return fxConverter.convert(native, c.currency || ccy, ccy) ?? native
            }),
        }))
        .sortBy('total')
        .reverse()
        .slice(0, 10)
        .value()

    const editingCost = editingCostId !== null && editingCostId !== 'new'
        ? entries.find(c => c.costId === editingCostId)
        : undefined

    async function handleSubmit(values: CostItem) {
        const saved = await postCost({ ...values, costCenter: values.costCenter || undefined })
        if (editingCostId === 'new') {
            await postCostLink({ costId: saved.costId!, entityId: IP_MANAGEMENT_ENTITY_ID, entity: IP_MANAGEMENT_ENTITY })
        }
        setEditingCostId(null)
    }

    async function handleDelete(cost: CostItem) {
        const link = linkByCostId[cost.costId!]
        if (link) await deleteCostLink({ ...link, costId: cost.costId! })
        await deleteCost(cost)
    }

    return (
        <>
            {/* @ts-ignore */}
            <Helmet>
                <title>Costs | Patent Cockpit</title>
            </Helmet>
            <div className="portfolio-menu max-w-2xl items-baseline">
                <IpManagementBreadcrumbs label="Costs" />
                <span className="text-slate-500 text-sm mr-auto">(in {ccy})</span>
            </div>
            <div className="main-content bg-pcx-100 pt-2">
                <div className="flex flex-row gap-2 items-center justify-end max-w-3xl mb-2">
                    <button type="button" className="btn-primary" onClick={() => setEditingCostId('new')}>
                        + Add cost
                    </button>
                </div>

                {entries.length > 0 ? <>
                    <div className="flex flex-row flex-wrap gap-4 mb-4">
                        <div className="min-w-xs max-w-xs p-4 bg-white rounded-lg shadow">
                            <CostsGraph byYear={byYear} />
                        </div>
                        <div className="min-w-xs max-w-xs p-4 bg-white rounded-lg shadow">
                            <CostsMainDriversGraph data={mainDrivers} />
                        </div>
                    </div>

                    <div className="p-4 bg-white rounded-md shadow w-fit overflow-x-auto">
                        <h3 id="costs" className="sm:pl-2 mb-2">Costs</h3>
                        <PureCostsTable
                            costs={sorted}
                            {...{ sortField, sortOrder, setSortField, setSortOrder }}
                            renderActions={c => <>
                                <button
                                    type="button"
                                    title="Edit"
                                    className="btn-secondary w-6 h-6 p-0.5"
                                    onClick={() => setEditingCostId(c.costId!)}
                                >
                                    <IconEdit />
                                </button>
                                <DeleteButton
                                    className="w-6 h-6 p-0.5 btn-warn-secondary"
                                    deleteAction={() => handleDelete(c)}
                                    question={`Delete cost "${c.reference || '(no reference)'}"?`}
                                />
                            </>}
                        />
                    </div>
                </> : (
                    <div className="p-4 bg-white rounded-md shadow w-fit my-4">
                        <h3>No costs</h3>
                        <p className="mt-2 mb-4 text-slate-600">Add your first IP management cost.</p>
                        <button type="button" className="btn-primary" onClick={() => setEditingCostId('new')}>+ Add cost</button>
                    </div>
                )}
            </div>

            {editingCostId !== null && (
                <Modal blurClick={() => setEditingCostId(null)} escAction={() => setEditingCostId(null)}>
                    <CostForm
                        initialValues={editingCost}
                        isEdit={editingCostId !== 'new'}
                        onSubmit={handleSubmit}
                        onCancel={() => setEditingCostId(null)}
                    />
                </Modal>
            )}
        </>
    )
}

function CostForm({ initialValues, isEdit, onSubmit, onCancel }: {
    initialValues?: CostItem,
    isEdit: boolean,
    onSubmit: (cost: CostItem) => void,
    onCancel: () => void,
}) {
    const defaults: CostItem = {
        date: new Date().toISOString().slice(0, 10),
        reference: '',
        costCenter: '',
        amount: 0,
        currency: 'EUR',
        vat: 0,
        comment: '',
    }

    return (
        <Formik initialValues={initialValues ?? defaults} onSubmit={onSubmit}>{({ values }) =>
            <Form>
                <CostFormFields values={values} isEdit={isEdit} topFields={<>
                    <div>
                        <label htmlFor="date" className="label mb-1">Date</label>
                        <Field id="date" name="date" required className="form-input" as={DatePicker} />
                    </div>
                    <div>
                        <label htmlFor="reference" className="label mb-1">Reference / Invoice #</label>
                        <Field id="reference" name="reference" className="form-input" />
                    </div>
                    <div>
                        <label htmlFor="costCenter" className="label mb-1">Cost center</label>
                        <Field id="costCenter" name="costCenter" className="form-input" />
                    </div>
                </>} />
                <CostFormFooter cancel={<button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>} />
            </Form>
        }</Formik>
    )
}
